import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useApiKeys = () => {
  const [tempApiKeys, setTempApiKeys] = useState<{ [key: number]: string }>({});
  const [tempOpenaiApiKeys, setTempOpenaiApiKeys] = useState<{ [key: number]: string }>({});
  const [tempGeminiApiKeys, setTempGeminiApiKeys] = useState<{ [key: number]: string }>({});
  const [showApiKeys, setShowApiKeys] = useState<{ [key: number]: boolean }>({});
  const [showOpenaiApiKeys, setShowOpenaiApiKeys] = useState<{ [key: number]: boolean }>({});
  const [showGeminiApiKeys, setShowGeminiApiKeys] = useState<{ [key: number]: boolean }>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  // Sincronização de estados
  useEffect(() => {
    const channel = supabase
      .channel('api_key_changes')
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'ai_agents' 
      }, (payload) => {
        const { new: newAgent } = payload;
        console.log('Atualização detectada:', newAgent);
        
        // Atualizar estados temporários
        if (newAgent.id) {
          setTempApiKeys(prev => ({
            ...prev,
            [newAgent.id]: newAgent.api_key || ''
          }));
          setTempOpenaiApiKeys(prev => ({
            ...prev,
            [newAgent.id]: newAgent.openai_api_key || ''
          }));
          setTempGeminiApiKeys(prev => ({
            ...prev,
            [newAgent.id]: newAgent.gemini_api_key || ''
          }));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const validateApiKey = (key: string, provider: string) => {
    if (!key) {
      throw new Error(`A chave API do ${provider} é obrigatória`);
    }
    if (key.length < 20) {
      throw new Error(`A chave API do ${provider} parece inválida (muito curta)`);
    }
  };

  const updateAgentApiKey = async (agentId: number, apiKey: string) => {
    setIsUpdating(true);
    try {
      validateApiKey(apiKey, 'Mistral');

      const { error } = await supabase
        .from('ai_agents')
        .update({ api_key: apiKey })
        .eq('id', agentId);

      if (error) throw error;
      
      setTempApiKeys(prev => ({
        ...prev,
        [agentId]: apiKey
      }));

      toast({
        title: "Sucesso",
        description: "Chave API do Mistral atualizada!",
      });
    } catch (error) {
      console.error('Erro ao atualizar chave Mistral:', error);
      toast({
        title: "Erro ao atualizar chave",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAgentOpenaiApiKey = async (agentId: number, openaiApiKey: string) => {
    setIsUpdating(true);
    try {
      validateApiKey(openaiApiKey, 'OpenAI');

      const { error } = await supabase
        .from('ai_agents')
        .update({ openai_api_key: openaiApiKey })
        .eq('id', agentId);

      if (error) throw error;
      
      setTempOpenaiApiKeys(prev => ({
        ...prev,
        [agentId]: openaiApiKey
      }));

      toast({
        title: "Sucesso",
        description: "Chave OpenAI atualizada!",
      });
    } catch (error) {
      console.error('Erro ao atualizar chave OpenAI:', error);
      toast({
        title: "Erro ao atualizar chave",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAgentGeminiApiKey = async (agentId: number, geminiApiKey: string) => {
    setIsUpdating(true);
    try {
      validateApiKey(geminiApiKey, 'Google Gemini');

      const { error } = await supabase
        .from('ai_agents')
        .update({ gemini_api_key: geminiApiKey })
        .eq('id', agentId);

      if (error) throw error;
      
      setTempGeminiApiKeys(prev => ({
        ...prev,
        [agentId]: geminiApiKey
      }));

      toast({
        title: "Sucesso",
        description: "Chave Gemini atualizada!",
      });
    } catch (error) {
      console.error('Erro ao atualizar chave Gemini:', error);
      toast({
        title: "Erro ao atualizar chave",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    tempApiKeys,
    setTempApiKeys,
    tempOpenaiApiKeys,
    setTempOpenaiApiKeys,
    tempGeminiApiKeys,
    setTempGeminiApiKeys,
    showApiKeys,
    setShowApiKeys,
    showOpenaiApiKeys,
    setShowOpenaiApiKeys,
    showGeminiApiKeys,
    setShowGeminiApiKeys,
    updateAgentApiKey,
    updateAgentOpenaiApiKey,
    updateAgentGeminiApiKey,
    isUpdating
  };
};